import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarDaysIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  UserGroupIcon,
  CheckBadgeIcon,
  ShareIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const EmployeeProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    // Simulate API call to fetch employee data
    const fetchEmployee = () => {
      const employeeData = {
        1: {
          id: 1,
          name: "Dr. Anita Reddy",
          position: "Cardiologist",
          department: "Cardiology",
          joinDate: "July 15, 2025",
          email: "anita.reddy@apollo.com",
          phone: "+91 98765 43210",
          location: "Block A, Floor 3, Room 305",
          image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=300&h=300&fit=crop&crop=face",
          status: "active",
          specialization: "Interventional Cardiology",
          experience: "12 years",
          bio: "Dr. Anita Reddy is a highly experienced interventional cardiologist with over 12 years of clinical practice. She specializes in complex cardiac procedures and has performed over 2,000 successful interventions.",
          education: [
            "MBBS - AIIMS, New Delhi (2011)",
            "MD Cardiology - PGIMER, Chandigarh (2015)",
            "Fellowship in Interventional Cardiology - Max Hospital (2017)"
          ],
          certifications: [
            "Board Certified Cardiologist",
            "Advanced Cardiac Life Support (ACLS)",
            "Interventional Cardiology Certification"
          ],
          skills: [
            "Cardiac Catheterization",
            "Angioplasty",
            "Stent Placement",
            "Echocardiography",
            "Cardiac Emergency Care"
          ],
          achievements: [
            "Outstanding Doctor Award 2024",
            "Best Research Paper - Indian Cardiology Conference 2023",
            "Excellence in Patient Care Award 2022"
          ],
          languages: ["English", "Hindi", "Telugu"],
          availability: "Mon-Fri: 9:00 AM - 5:00 PM",
          emergencyContact: "+91 98765 43210"
        },
        2: {
          id: 2,
          name: "Arjun Singh",
          position: "Nurse Practitioner",
          department: "Emergency Medicine",
          joinDate: "July 18, 2025",
          email: "arjun.singh@apollo.com",
          phone: "+91 98765 43211",
          location: "Emergency Wing, Trauma Bay 2",
          image: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=300&h=300&fit=crop&crop=face",
          status: "active",
          specialization: "Critical Care",
          experience: "8 years",
          bio: "Arjun Singh is a dedicated nurse practitioner specializing in emergency and critical care. With 8 years of experience, he has handled numerous critical cases and is known for his quick decision-making skills.",
          education: [
            "BSc Nursing - Christian Medical College, Vellore (2015)",
            "MSc Critical Care Nursing - Manipal University (2018)"
          ],
          certifications: [
            "Critical Care Registered Nurse (CCRN)",
            "Emergency Nursing Certification (CEN)",
            "Basic Life Support (BLS) Instructor"
          ],
          skills: [
            "Critical Care Management", 
            "Emergency Response",
            "Patient Assessment",
            "Medication Administration",
            "Life Support Systems"
          ],
          achievements: [
            "Nurse Excellence Award 2024",
            "Critical Care Hero Award 2023",
            "Team Leadership Recognition 2022"
          ],
          languages: ["English", "Hindi", "Punjabi"],
          availability: "24/7 Emergency Coverage",
          emergencyContact: "+91 98765 43211"
        }
      };

      setTimeout(() => {
        setEmployee(employeeData[id] || null);
        setLoading(false);
      }, 1000);
    };

    fetchEmployee();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-apollo-gray">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading employee profile...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen bg-apollo-gray">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Employee Not Found</h2>
            <p className="text-gray-600 mb-6">The employee profile you're looking for doesn't exist.</p>
            <button
              onClick={() => navigate('/directory')}
              className="bg-apollo-accent text-white px-6 py-3 rounded-xl hover:bg-apollo-accent/90 transition-colors"
            >
              Back to Directory
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: UserGroupIcon },
    { id: 'contact', label: 'Contact', icon: EnvelopeIcon },
    { id: 'experience', label: 'Experience', icon: BriefcaseIcon },
    { id: 'education', label: 'Education', icon: AcademicCapIcon }
  ];

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <button
          onClick={() => navigate('/directory')}
          className="flex items-center space-x-2 text-apollo-accent hover:text-apollo-primary transition-colors mb-6"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span className="font-medium">Back to Directory</span>
        </button>
      </div>

      {/* Profile Header */}
      <div className="bg-gradient-to-r from-apollo-primary to-apollo-accent text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-6 lg:space-y-0 lg:space-x-8">
            {/* Profile Image */}
            <div className="relative">
              <img
                src={employee.image}
                alt={employee.name}
                className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl object-cover border-4 border-white shadow-2xl"
              />
              <div className="absolute -bottom-1 sm:-bottom-2 -right-1 sm:-right-2 w-6 h-6 sm:w-8 sm:h-8 bg-green-400 rounded-full border-4 border-white flex items-center justify-center">
                <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-600 rounded-full animate-pulse"></div>
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex-1 text-center lg:text-left">
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start space-y-2 sm:space-y-0 sm:space-x-3 mb-2">
                <h1 className="text-2xl sm:text-3xl font-bold">{employee.name}</h1>
                <CheckBadgeIcon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-200" />
              </div>
              <p className="text-lg sm:text-xl text-blue-100 mb-2">{employee.position}</p>
              <p className="text-blue-200 mb-4">{employee.department}</p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start space-y-2 sm:space-y-0 sm:space-x-6 text-blue-100 text-sm sm:text-base">
                <div className="flex items-center space-x-2">
                  <MapPinIcon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{employee.location}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CalendarDaysIcon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">Joined {employee.joinDate}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <BriefcaseIcon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{employee.experience} experience</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row lg:flex-col space-y-3 sm:space-y-0 sm:space-x-3 lg:space-x-0 lg:space-y-3">
              <button className="bg-white text-apollo-primary px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base">
                <ChatBubbleLeftRightIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Message</span>
              </button>
              <button className="border-2 border-white text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold hover:bg-white hover:text-apollo-primary transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base">
                <ShareIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Share Profile</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-b border-gray-200 bg-white rounded-t-2xl -mt-8 pt-6 sm:pt-8">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-apollo-accent text-apollo-accent'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-b-2xl shadow-lg p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8">
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Bio */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">About</h3>
                <p className="text-gray-600 leading-relaxed">{employee.bio}</p>
              </div>

              {/* Skills */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Core Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {employee.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="bg-apollo-accent/10 text-apollo-accent px-3 py-1 rounded-full text-sm font-medium"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* Achievements */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Achievements</h3>
                <div className="space-y-3">
                  {employee.achievements.map((achievement, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckBadgeIcon className="w-5 h-5 text-apollo-accent mt-0.5" />
                      <span className="text-gray-600">{achievement}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Languages</h3>
                <div className="flex flex-wrap gap-2">
                  {employee.languages.map((language, index) => (
                    <span
                      key={index}
                      className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                    >
                      {language}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div className="bg-gray-50 p-4 sm:p-6 rounded-xl">
                  <div className="flex items-center space-x-3 mb-3">
                    <EnvelopeIcon className="w-5 h-5 sm:w-6 sm:h-6 text-apollo-accent" />
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Email</h3>
                  </div>
                  <p className="text-gray-600 text-sm sm:text-base break-all">{employee.email}</p>
                </div>

                <div className="bg-gray-50 p-4 sm:p-6 rounded-xl">
                  <div className="flex items-center space-x-3 mb-3">
                    <PhoneIcon className="w-5 h-5 sm:w-6 sm:h-6 text-apollo-accent" />
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Phone</h3>
                  </div>
                  <p className="text-gray-600 text-sm sm:text-base">{employee.phone}</p>
                </div>

                <div className="bg-gray-50 p-4 sm:p-6 rounded-xl">
                  <div className="flex items-center space-x-3 mb-3">
                    <MapPinIcon className="w-5 h-5 sm:w-6 sm:h-6 text-apollo-accent" />
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Location</h3>
                  </div>
                  <p className="text-gray-600 text-sm sm:text-base">{employee.location}</p>
                </div>

                <div className="bg-gray-50 p-4 sm:p-6 rounded-xl">
                  <div className="flex items-center space-x-3 mb-3">
                    <CalendarDaysIcon className="w-5 h-5 sm:w-6 sm:h-6 text-apollo-accent" />
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Availability</h3>
                  </div>
                  <p className="text-gray-600 text-sm sm:text-base">{employee.availability}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'experience' && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Specialization</h3>
                <div className="bg-apollo-accent/5 border border-apollo-accent/20 p-6 rounded-xl">
                  <p className="text-apollo-accent font-semibold text-lg">{employee.specialization}</p>
                  <p className="text-gray-600 mt-2">{employee.experience} of professional experience</p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Certifications</h3>
                <div className="space-y-3">
                  {employee.certifications.map((cert, index) => (
                    <div key={index} className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg">
                      <CheckBadgeIcon className="w-5 h-5 text-apollo-accent mt-0.5" />
                      <span className="text-gray-700">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'education' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Educational Background</h3>
              <div className="space-y-4">
                {employee.education.map((edu, index) => (
                  <div key={index} className="flex items-start space-x-4 bg-gray-50 p-6 rounded-xl">
                    <AcademicCapIcon className="w-6 h-6 text-apollo-accent mt-1" />
                    <div>
                      <p className="text-gray-800 font-medium">{edu}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EmployeeProfile;
